import React, { useEffect, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Routes, Route, useLocation } from "react-router-dom";
import { Slide, ToastContainer } from "react-toastify";
import PropTypes from 'prop-types';

import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import { useStyles } from "./common/Styles/AppStyles";

// Constans
import { API_RESPONSE_STATUS, APP_ROUTES, CURRENT_COUNTRY, NEW_PATH_SELECTED, SIDE_MENU_LABEL, USER_INFO_DOS } from "../config/constants";

// actions
import {
  fetchAllAreasOfCountry,
  fetchAllCollectionPointsTypes,
  fetchAllRegionsOfCountry,
  fetchAllUserRolesTypes,
  fetchListOfCountries,
  fetchPOSTypes,
  fetchServiceInActiveStatus,
  fetchServicePropositionRecord,
  fetchServiceTypes,
  selectAllActiveInActiveServiceTypeRecordService
} from "../config/redux/configurationsSlice";
import { isArrayValid, isEmptyOrNull } from "./../utils/DataUtils";
import { ReactComponent as CloseIconButton } from "./assets/svg/closeButton.svg";

// Common Components
import { CountryRoute, PosRoute, PrivateRoute, RuleEngineRoute, DeliveryFeeRoute } from "./common/PrivateRoute";
import { fetchCountrySettings, selectCountrySettings } from "../components/CountrySettings/redux/countrySettingsSlice";
import CreatePOS from "./CreatePOS/CreatePOS";
import { validateUserToken } from "./Login/redux/userSlice";
import { fetchAllAdditionalServicesTypes } from "./UserManagement/Redux/UserManagementSlice";

// Custom Components
import TopMenu from "./common/TopMenu/TopMenu";
import SideMenu from "./common/SideMenu/SideMenu";
import Loader from "./common/Loader/Loader";

// Route Component
import SSOLoginPage from "./Login/sso/SSOLoginPage";
import POSList from "./POSList/POSList";
import CompleteSetup from "./CompleteSetup/CompleteSetup";
import CountrySettings from "./CountrySettings/CountrySettings";
import CurrentConfiguration from "./RuleEnginePage/CurrentConfiguration/CurrentCofiguration";
import RuleEngine from "./RuleEnginePage/RuleEnginePage";
import UserManagementPage from "./UserManagement/UserManagementPage";
import DeliveryFeePage from "./DeliveryFee/DeliveryFeePage";
import AddUpdateDlvFeeConfigPage from "./DeliveryFee/Configuration/AddUpdateDlvFeeConfigPage";
import ConfigurationPreview from "./DeliveryFee/ConfigurationPreview";
import { changeCountry } from "config/redux/appConfigSlice";
import { getRuleEngineItems, getTopBarMenuItems } from "./common/TopMenu/TopMenuUtil";

// December 2022
import ApprovalRequestRecordPage from "./ApprovalRequest/ApprovalRequestRecordPage";
import NewRequestForServiceAndSlot from "./ApprovalRequest/newRequest/NewRequestForServiceAndSlot";
import ApprovalRequestDetailsPage from "./ApprovalRequest/ApprovalRequestDetailsPage";
import { fetchPendingApprovalRequest, selectAllPendingApprovalRequestRecordListServices } from "./ApprovalRequest/Redux/ApprovalRequestSlice";

// May 2023
import PlolygonServicesPage from "./PlolygonServices/PlolygonServicesPage";
import { findTerm, generateElementIdentifier } from "../utils/ApplicationUtils";

// Dashboard
import DashboardPage from "./Dashboard/DashboardPage";
import PlolygonServicesConfigurationPage from "./PlolygonServices/PlolygonServicesConfigurationPage";
import { useAppNavigation } from "../utils/navigation";
import { DATA_IDENTIFIER, ELEMENT } from "./common/App/app-constant";

const ToastCloseIcon = ({ closeToast }) => {
  return <CloseIconButton onClick={closeToast} className="close-icon" />;
};

// Add PropTypes for validation
ToastCloseIcon.propTypes = {
  closeToast: PropTypes.func.isRequired,
};

const App = () => {
  const { pathname } = useLocation();

  const classes = useStyles();
  const dispatch = useDispatch();

  const { goToLoginAndReplace, goToPath } = useAppNavigation();
  const [appState, setAppState] = useState({
    isLoading: false,
    inValiduser: false,
    openSideMenu: false,
    SideBarMenuItemsList: [],
    selectedIndex: 0,
    openRuleEngineMenu: false,
    ruleEngineItems: [],
  });

  // Selectors Start
  const { isLoggedIn, userDetails } = useSelector((state) => state.user);
  const { fetchStatus: countrySettingsFetchStatus } = useSelector(selectCountrySettings);
  const { countries, serviceTypes, posTypes, regions, areas, collectionPointsTypes, roles, servicePropositionRecord } = useSelector((state) => state.configurations, shallowEqual);
  const { additionalServices } = useSelector((state) => state.userMgmtConfigurations, shallowEqual);
  const pendingApprovalRequestRecordListService = useSelector(selectAllPendingApprovalRequestRecordListServices); // Approval Flow, Show pending record in the side menu
  const selectAllActiveInActiveServiceTypeRecord = useSelector(selectAllActiveInActiveServiceTypeRecordService);
  // Selectors End

  let userInfo = localStorage.getItem(USER_INFO_DOS);
  userInfo = !isEmptyOrNull(userInfo) ? JSON.parse(userInfo) : userInfo;

  const mouseOverEventOnSideMenu = (event) => {
    event.preventDefault();
    if (!appState.openSideMenu) {
      const currentLocation = window.location.pathname;
      let openRuleEngineMenu = false;
      let selectedIndex = appState.selectedIndex;
      if (!isEmptyOrNull(currentLocation)) {
        if (currentLocation.includes(APP_ROUTES.DELIVERY_FEE) || currentLocation.includes(APP_ROUTES.RULE_ENGINE) || currentLocation.includes(APP_ROUTES.SINGLE_USE_PLASTIC)) {
          openRuleEngineMenu = true;
          if (currentLocation.includes(APP_ROUTES.RULE_ENGINE)) {
            selectedIndex = 6;
          } else if (currentLocation.includes(APP_ROUTES.DELIVERY_FEE)) {
            selectedIndex = 7;
          } else if (currentLocation.includes(APP_ROUTES.SINGLE_USE_PLASTIC)) {
            selectedIndex = 8;
          }
        }
      }
      setAppState({ ...appState, openSideMenu: true, openRuleEngineMenu: openRuleEngineMenu, selectedIndex: selectedIndex });
    }
    window.dispatchEvent(new CustomEvent("resize"));
  };

  const mouseOverEventOnMainArea = (event) => {
    event.preventDefault();
    if (appState.openSideMenu) {
      const currentLocation = window.location.pathname;
      let selectedIndex = getSelectedIndexOnFirstLoad(currentLocation);
      setAppState({ ...appState, openSideMenu: false, openRuleEngineMenu: false, selectedIndex: selectedIndex });
      window.dispatchEvent(new CustomEvent("resize"));
    }
  };

  const fetch_application_record_list = (selected_country_obj) => {
    dispatch(changeCountry(selected_country_obj));
    dispatch(fetchCountrySettings(selected_country_obj));

    if (regions.fetchStatus === API_RESPONSE_STATUS.IDLE && regions.fetchStatus !== API_RESPONSE_STATUS.LOADING) {
      dispatch(fetchAllRegionsOfCountry(selected_country_obj?.countryId));
    }
    if (areas.fetchStatus === API_RESPONSE_STATUS.IDLE && areas.fetchStatus !== API_RESPONSE_STATUS.LOADING) {
      dispatch(fetchAllAreasOfCountry(selected_country_obj?.countryId));
    }
    if (serviceTypes.fetchStatus === API_RESPONSE_STATUS.IDLE) {
      dispatch(fetchServiceTypes());
    }
    if (posTypes.fetchStatus === API_RESPONSE_STATUS.IDLE) {
      dispatch(fetchPOSTypes());
    }
    if (collectionPointsTypes.fetchStatus === API_RESPONSE_STATUS.IDLE) {
      dispatch(fetchAllCollectionPointsTypes());
    }
    if (roles.fetchStatus === API_RESPONSE_STATUS.IDLE) {
      dispatch(fetchAllUserRolesTypes());
    }
    if (additionalServices.fetchStatus === API_RESPONSE_STATUS.IDLE) {
      dispatch(fetchAllAdditionalServicesTypes());
    }
    if (servicePropositionRecord.fetchStatus === API_RESPONSE_STATUS.IDLE) {
      dispatch(fetchServicePropositionRecord());
    }

    dispatch(fetchPendingApprovalRequest({ countryId: selected_country_obj?.countryId, status: ["PENDING"] }));
    dispatch(fetchServiceInActiveStatus(selected_country_obj?.countryId));
  };

  const _fetch_country_list_record = () => {
    if (countries.fetchStatus === API_RESPONSE_STATUS.IDLE) {
      dispatch(fetchListOfCountries());
    }
  };

  const handleListItemClick = (event, selected_index, toLink) => {
    setAppState({ ...appState, selectedIndex: selected_index });
    localStorage.setItem(NEW_PATH_SELECTED, toLink);
  };

  const getSelectedIndexOnFirstLoad = (_new_path_name) => {
    if (!isEmptyOrNull(_new_path_name)) {
      switch (_new_path_name) {
        case APP_ROUTES.DASHBOARD:
          return 0;
        case findTerm(_new_path_name, APP_ROUTES.POS_LIST):
        case findTerm(_new_path_name, APP_ROUTES.COMPLETE_SETUP):
        case findTerm(_new_path_name, APP_ROUTES.CREATE_POS):
          return 1;
        case findTerm(_new_path_name, APP_ROUTES.RULE_ENGINE):
        case findTerm(_new_path_name, APP_ROUTES.DELIVERY_FEE):
        case findTerm(_new_path_name, APP_ROUTES.SINGLE_USE_PLASTIC):
          return 2;
        case findTerm(_new_path_name, APP_ROUTES.POLYGON_SERVICES):
          return 3;
        case findTerm(_new_path_name, APP_ROUTES.APPROVAL_REQUESTS):
          return 4;
        case findTerm(_new_path_name, APP_ROUTES.USER_MANAGEMENT):
          return 5;
        default:
          return 0;
      }
    }
  };

  const checkUserInfo = async () => {
    if (!isLoggedIn && userInfo?.token) {
      let response = await dispatch(validateUserToken(userInfo.token));
      if (!isEmptyOrNull(response?.payload?.error)) {
        setAppState({ ...appState, inValiduser: true });
      }
    }
    if (isLoggedIn) {
      setAppState({ ...appState, isLoading: true });
      _fetch_country_list_record();
    } else {
      goToLoginAndReplace();
    }
  };

  const update_country_value = () => {
    let selected_country_obj = null;
    let storedCountry = !isEmptyOrNull(localStorage.getItem(CURRENT_COUNTRY)) ? JSON.parse(localStorage.getItem(CURRENT_COUNTRY)) : null;
    if (!isEmptyOrNull(storedCountry?.countryId) && !isEmptyOrNull(userDetails?.userProfile?.countryId) && isArrayValid(userDetails?.userProfile?.countryId) && userDetails.userProfile.countryId.includes(storedCountry?.countryId)) {
      selected_country_obj = storedCountry;
    } else {
      if (!isEmptyOrNull(userDetails?.userProfile?.countryId) && isArrayValid(userDetails?.userProfile?.countryId)) {
        let userAccessedCountries = countries.data.filter((item) => userDetails.userProfile.countryId.includes(item?.countryId));
        userAccessedCountries.forEach((country) => {
          if (isEmptyOrNull(selected_country_obj) && !isEmptyOrNull(country?.countryId) && isArrayValid(countries?.data) && countries.data.includes(country)) {
            selected_country_obj = country;
          }
        });
      } else {
        selected_country_obj = countries.data[0];
      }
      localStorage.setItem(CURRENT_COUNTRY, JSON.stringify(selected_country_obj));
    }
    if (!isEmptyOrNull(selected_country_obj)) {
      fetch_application_record_list(selected_country_obj);
    }
  };

  const handleMultiOptionListItemClick = (event, sideItemIndex, collapsable_list_item_name) => {
    if (!isEmptyOrNull(collapsable_list_item_name)) {
      if (collapsable_list_item_name === SIDE_MENU_LABEL.RULE_ENGINE_SET_UP) {
        setAppState({ ...appState, selectedIndex: sideItemIndex, openRuleEngineMenu: !appState.openRuleEngineMenu });
      }
    }
  };

  useEffect(() => {
    const allDataLoaded = [countries, serviceTypes, posTypes, regions, roles, additionalServices].every((data) => data?.fetchStatus !== API_RESPONSE_STATUS.IDLE);
    if (isLoggedIn && allDataLoaded) {
      let _new_path_name = APP_ROUTES.DASHBOARD;
      if (!isEmptyOrNull(localStorage.getItem(NEW_PATH_SELECTED))) {
        _new_path_name = localStorage.getItem(NEW_PATH_SELECTED);
      } else if (pathname !== APP_ROUTES.LOGIN) {
        _new_path_name = pathname;
      }

      goToPath(_new_path_name);
      setAppState({
        ...appState,
        isLoading: false,
        selectedIndex: getSelectedIndexOnFirstLoad(_new_path_name),
        SideBarMenuItemsList: !isEmptyOrNull(userDetails?.userProfile) ? getTopBarMenuItems(userDetails.userProfile) : [],
        ruleEngineItems: !isEmptyOrNull(userDetails?.userProfile) ? getRuleEngineItems(userDetails.userProfile) : [],
      });
    }
  }, [isLoggedIn, countries.fetchStatus && serviceTypes.fetchStatus && posTypes.fetchStatus && regions.fetchStatus && roles.fetchStatus && additionalServices.fetchStatus]);

  useEffect(() => {
    isArrayValid(countries?.data) && countrySettingsFetchStatus === API_RESPONSE_STATUS.IDLE && update_country_value();
  }, [countries.data, countries.data.length, countrySettingsFetchStatus]);

  useEffect(() => {
    isLoggedIn !== undefined && checkUserInfo();
  }, [isLoggedIn]);

  return (
    <>
      {!appState.inValiduser && appState.isLoading ? (
        <Loader />
      ) : (
        <div className={classes.root} data-test="AppComponent">
          {!isEmptyOrNull(userDetails?.userProfile?.role) && pathname !== APP_ROUTES.LOGIN && (
            <>
              <TopMenu data-testid={generateElementIdentifier({ key: DATA_IDENTIFIER.TOP_MENU, element: ELEMENT.BUTTON })} handleListItemClick={handleListItemClick} selectAllActiveInActiveServiceTypeRecord={selectAllActiveInActiveServiceTypeRecord} />
              <SideMenu
                data-testid={generateElementIdentifier({ key: DATA_IDENTIFIER.SIDE_MENU, element: ELEMENT.DIV })}
                openSideMenu={appState.openSideMenu}
                classes={classes}
                SideBarMenuItemsList={appState.SideBarMenuItemsList}
                mouseOverEventOnSideMenu={mouseOverEventOnSideMenu}
                selectedIndex={appState.selectedIndex}
                handleListItemClick={handleListItemClick}
                pendingApprovalRequestRecordListService={pendingApprovalRequestRecordListService}
                userDetails={userDetails}
                handleMultiOptionListItemClick={handleMultiOptionListItemClick}
                openRuleEngineMenu={appState.openRuleEngineMenu}
                ruleEngineItems={appState.ruleEngineItems}
              />
            </>
          )}
          <main data-testid={generateElementIdentifier({ key: DATA_IDENTIFIER.MAIN, element: ELEMENT.DIV })} className={classes.content} onMouseOver={mouseOverEventOnMainArea} onFocus={mouseOverEventOnMainArea}>
            <div className={classes.mainRouteDiv}>
              <Routes>
                <Route path={APP_ROUTES.LOGIN} element={<SSOLoginPage />} />
                {/* Country Setting */}
                <Route path={`${APP_ROUTES.COUNTRY_SETTINGS}/:countryName`} element={
                  <CountryRoute userProfile={userDetails}>
                    <CountrySettings />
                  </CountryRoute>
                } />
                {/* POS routes */}
                <Route path={`${APP_ROUTES.POS_LIST}/*`} element={
                  <PrivateRoute>
                    <POSList />
                  </PrivateRoute>
                } />
                <Route path={`${APP_ROUTES.CREATE_POS}/*`} element={
                  <PosRoute userRole={userDetails?.userProfile?.role}>
                    <CreatePOS />
                  </PosRoute>
                } />
                <Route path={`${APP_ROUTES.COMPLETE_SETUP}/:posNo`} element={
                  <PosRoute userRole={userDetails?.userProfile?.role}>
                    <CompleteSetup />
                  </PosRoute>
                } />
                {/* Approval Request */}
                <Route path={`${APP_ROUTES.NEW_APPROVAL_REQUESTS}/*`} element={
                  <PrivateRoute>
                    <NewRequestForServiceAndSlot />
                  </PrivateRoute>
                } />
                <Route path={`${APP_ROUTES.APPROVAL_REQUESTS}/*`} element={
                  <PrivateRoute>
                    <ApprovalRequestRecordPage />
                  </PrivateRoute>
                } />
                <Route path={`${APP_ROUTES.APPROVAL_REQUESTS_DETAILS}/*`} element={
                  <PrivateRoute>
                    <ApprovalRequestDetailsPage />
                  </PrivateRoute>
                } />
                {/* Rule Engine Routes */}
                <Route path={`${APP_ROUTES.RULE_ENGINE}/*`} element={
                  <RuleEngineRoute userProfile={userDetails}>
                    <CurrentConfiguration />
                  </RuleEngineRoute>
                } />
                <Route path={`${APP_ROUTES.RULE_ENGINE_CURRENT_CONFIGURATION}/:serviceName?`} element={
                  <RuleEngineRoute userProfile={userDetails}>
                    <RuleEngine />
                  </RuleEngineRoute>
                } />
                {/* User Routes */}
                <Route path={`${APP_ROUTES.USER_MANAGEMENT}/*`} element={
                  <PrivateRoute>
                    <UserManagementPage />
                  </PrivateRoute>
                } />
                {/* Delivery Fee  */}
                <Route path={`${APP_ROUTES.DELIVERY_FEE}/*`} element={
                  <DeliveryFeeRoute userRole={userDetails?.userProfile?.role}>
                    <DeliveryFeePage />
                  </DeliveryFeeRoute>
                } />
                <Route path={`${APP_ROUTES.DELIVERY_FEE_CONFIGURATION}/*`} element={
                  <DeliveryFeeRoute userRole={userDetails?.userProfile?.role}>
                    <AddUpdateDlvFeeConfigPage />
                  </DeliveryFeeRoute>
                } />
                <Route path={`${APP_ROUTES.DELIVERY_FEE_PREVIEW_CONFIGURATION}/*`} element={
                  <DeliveryFeeRoute userRole={userDetails?.userProfile?.role}>
                    <ConfigurationPreview />
                  </DeliveryFeeRoute>
                } />
                {/* Polygon Services  */}
                <Route path={`${APP_ROUTES.POLYGON_SERVICES}/*`} element={
                  <PrivateRoute>
                    <PlolygonServicesPage />
                  </PrivateRoute>
                } />
                <Route path={`${APP_ROUTES.POLYGON_SERVICES_CONFIGURATION}/*`} element={
                  <PrivateRoute>
                    <PlolygonServicesConfigurationPage />
                  </PrivateRoute>
                } />
                {/* Dashboard Services  */}
                <Route path={`${APP_ROUTES.DASHBOARD}/*`} element={
                  <PrivateRoute>
                    <DashboardPage />
                  </PrivateRoute>
                } />
              </Routes>
            </div>
          </main>
        </div>
      )}
      <ToastContainer data-testid={generateElementIdentifier({ key: DATA_IDENTIFIER.TOAST_CONTAINER, element: ELEMENT.DIV })} position="top-center" transition={Slide} hideProgressBar closeButton={ToastCloseIcon} autoClose={5000} />
    </>
  );
};

export default React.memo(App);
